import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Heading,
  Select,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import AddEditRequirementModal from "../../ManageRequirements/AddEditRequirementModal";
import TaskTable from "../../ManageRequirements/Tasks/TaskTable";
import BulkAddRequirementsModal from "../../ManageRequirements/BulkAddRequirementsModal";
import DeliverableAccordion from "./DeliverableAccordion";
import RequirementsTable from "./RequirementsTable";
import { AddIcon } from "@chakra-ui/icons";
import { useData } from "../../../Contexts/DataContext";
import { useResponsive } from "../../../Contexts/ResponsiveContext";

function Tracker() {
  const { isMobile } = useResponsive();
  const {
    isOpen: isBulkModalOpen,
    onOpen: onBulkModalOpen,
    onClose: onBulkModalClose,
  } = useDisclosure();

  const {
    isOpen: isViewModalOpen,
    onOpen: onViewModalOpen,
    onClose: onViewModalClose,
  } = useDisclosure();

  // ✅ Local UI state
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [viewMode, setViewMode] = useState("task"); // "req", "del", or "task"
  const [statusFilter, setStatusFilter] = useState("all");
  const [deliverableFilter, setDeliverableFilter] = useState("all");
  const [requirementFilter, setRequirementFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState("all");
  const [isNewRequirement, setIsNewRequirement] = useState(false);

  // ✅ Context data from useData (assumed to be already set up)
  const { developers = [], enrichedSow, enrichedTasks = [] } = useData();

  // ✅ Flatten all deliverables
  const allDeliverables = useMemo(() => {
    return (
      enrichedSow?.deliverables?.map((d) => ({
        id: d.id,
        name: d.name,
        status: d.status || "Pending",
        targetDate: d.targetDate,
        assigned: d.assigned,
        requirements: d.requirements || [],
      })) || []
    );
  }, [enrichedSow]);

  // ✅ Filter deliverables (for Deliverable view)
  const filteredDeliverables = useMemo(() => {
    return allDeliverables.filter((del) => {
      const matchesStatus =
        statusFilter === "all" ||
        del.status?.toLowerCase() === statusFilter.toLowerCase();
      const matchesDeliverable =
        deliverableFilter === "all" || del.id === deliverableFilter;

      return (viewMode === "del" ? matchesStatus : true) && matchesDeliverable;
    });
  }, [allDeliverables, statusFilter, deliverableFilter, viewMode]);

  // ✅ Flatten all requirements
  const allRequirements = useMemo(() => {
    return allDeliverables.flatMap((d) => d.requirements || []);
  }, [allDeliverables]);

  // ✅ Filter requirements (for Requirement or Task view)
  const filteredRequirements = useMemo(() => {
    return allRequirements.filter((req) => {
      const matchesStatus =
        statusFilter === "all" ||
        req.status?.toLowerCase() === statusFilter.toLowerCase();
      const matchesDeliverable =
        deliverableFilter === "all" || req.deliverableId === deliverableFilter;
      const matchesRequirement =
        requirementFilter === "all" || req.id === requirementFilter;
      const matchesAssigned =
        assignedToFilter === "all" ||
        req.assignedTo?.toLowerCase() === assignedToFilter.toLowerCase();

      const shouldFilterAssigned = viewMode === "req" ? matchesAssigned : true;

      return viewMode === "req" || viewMode === "task"
        ? matchesStatus &&
            matchesDeliverable &&
            matchesRequirement &&
            shouldFilterAssigned
        : true;
    });
  }, [
    allRequirements,
    statusFilter,
    deliverableFilter,
    requirementFilter,
    assignedToFilter,
    viewMode,
  ]);

  const filteredTasks = useMemo(() => {
    return enrichedTasks.filter((task) => {
      const matchesDeliverable =
        deliverableFilter === "all" || task.deliverableId === deliverableFilter;

      const matchesRequirement =
        requirementFilter === "all" || task.requirementId === requirementFilter;

      const matchesAssigned =
        assignedToFilter === "all" ||
        task.assignedTo?.toLowerCase() === assignedToFilter.toLowerCase();

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "completed" && task.completed) ||
        (statusFilter !== "completed" &&
          !task.completed &&
          task.status?.toLowerCase() === statusFilter.toLowerCase());

      return (
        matchesDeliverable &&
        matchesRequirement &&
        matchesAssigned &&
        matchesStatus
      );
    });
  }, [
    enrichedTasks,
    deliverableFilter,
    requirementFilter,
    assignedToFilter,
    statusFilter,
  ]);

  // console.log("filteredRequirements", filteredRequirements)

  const uniqueAssignees = useMemo(() => {
    if (viewMode === "task") {
      return Array.from(
        new Set(
          filteredRequirements
            .flatMap((r) => r.tasks || [])
            .map((t) => t.assignedTo)
            .filter(Boolean)
        )
      );
    } else {
      return Array.from(
        new Set(filteredRequirements.map((r) => r.assignedTo).filter(Boolean))
      );
    }
  }, [filteredRequirements, viewMode]);

  const handleOpenRequirement = (requirement) => {
    setSelectedRequirement(requirement);
    setIsNewRequirement(false); 
    onViewModalOpen();
  };

  const handleNewRequirement = () => {
    setSelectedRequirement(null); 
    setIsNewRequirement(true); 
    onViewModalOpen();
  };

  const handleCloseModal = () => {
    setSelectedRequirement(null);
    setIsNewRequirement(false);
    onViewModalClose();
  };

  return (
    <Box flex={1} w="100%" p={2}>
      <Flex justifyContent={"space-between"} align={"center"}>
        <HStack>
          <Heading color={"black"} fontSize={"xl"}>
            Project Tracker
          </Heading>
          {/* <Badge boxShadow={"md"}  fontSize={"sm"} colorScheme="purple">
            {selectedSow?.projectName}
          </Badge> */}
        </HStack>
        <ButtonGroup>
          <Button
            leftIcon={<AddIcon />}
            fontSize={"xs"}
            colorScheme={"yellow"}
            onClick={handleNewRequirement} // Updated to handle new requirement
          >
            New Request
          </Button>
          {!isMobile && (
            <Button
              leftIcon={<AddIcon />}
              fontSize={"xs"}
              colorScheme={"yellow"}
              onClick={onBulkModalOpen}
            >
              Bulk Add Requirements
            </Button>
          )}
        </ButtonGroup>
      </Flex>

      {enrichedSow && (
        <Box
          overflowY={"auto"}
          mt={4}
          borderRadius={"md"}
          borderColor={"gray.200"}
          // bg={"black"}
        >
          <Flex gap={4} mb={8} direction={isMobile ? "column" : "row"}>
            <HStack>
              <VStack align="start" mr={8}>
                <Text color={"black"} fontSize={"xs"}>
                  View by:
                </Text>
                <ButtonGroup isAttached size="sm">
                  {/* <Button
                  fontSize={"xs"}
                  color={viewMode === "del" ? "white" : "black"}
                  _hover={{
                    bg: viewMode === "del" ? "undefined" : "gray.100",
                  }}
                  variant={viewMode === "del" ? "solid" : "outline"}
                  colorScheme={viewMode === "del" ? "blue" : "gray"}
                  onClick={() => setViewMode("del")}
                >
                  Deliverable
                </Button> */}
                  <Button
                    fontSize={"xs"}
                    color={viewMode === "req" ? "white" : "black"}
                    _hover={{
                      bg: viewMode === "req" ? "undefined" : "gray.100",
                    }}
                    variant={viewMode === "req" ? "solid" : "outline"}
                    bg={viewMode === "req" ? "tomato" : "white"}
                    onClick={() => setViewMode("req")}
                  >
                    Requirements
                  </Button>
                  <Button
                    fontSize={"xs"}
                    color={viewMode === "task" ? "white" : "black"}
                    _hover={{
                      bg: viewMode === "task" ? "undefined" : "gray.100",
                    }}
                    variant={viewMode === "task" ? "solid" : "outline"}
                    bg={viewMode === "task" ? "tomato" : "white"}
                    onClick={() => setViewMode("task")}
                  >
                    Tasks
                  </Button>
                </ButtonGroup>
              </VStack>
              {!isMobile && (
                <>
                  <VStack align="start">
                    <Text color={"black"} fontSize={"xs"}>
                      Deliverable:
                    </Text>
                    <Select
                      size="sm"
                      value={deliverableFilter}
                      onChange={(e) => setDeliverableFilter(e.target.value)}
                      width="400px"
                    >
                      <option value="all">All</option>
                      {enrichedSow?.deliverables?.map((deliverable) => (
                        <option key={deliverable.id} value={deliverable.id}>
                          {deliverable.name || `Deliverable ${deliverable.id}`}
                        </option>
                      ))}
                    </Select>
                  </VStack>
                  <VStack align="start">
                    <Text fontSize="xs">Requirement:</Text>
                    <Select
                      size="sm"
                      value={requirementFilter}
                      onChange={(e) => setRequirementFilter(e.target.value)}
                      width="400px"
                    >
                      <option value="all">All</option>
                      {filteredRequirements.map((req) => (
                        <option key={req.id} value={req.id}>
                          {req.title || `Requirement ${req.id}`}
                        </option>
                      ))}
                    </Select>
                  </VStack>

                  {viewMode === "req" && (
                    <VStack align="start">
                      <Text color={"black"} fontSize={"xs"}>
                        Requirement Status:
                      </Text>
                      <Select
                        size="sm"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        width="200px"
                      >
                        <option value="all">All</option>
                        {/* <option value="pending">Pending</option> */}
                        <option value="not started">Not Started</option>
                        <option value="in progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </Select>
                    </VStack>
                  )}
                  <VStack align="start">
                    <Text fontSize="xs">Assigned To:</Text>
                    <Select
                      size="sm"
                      value={assignedToFilter}
                      onChange={(e) => setAssignedToFilter(e.target.value)}
                      width="200px"
                    >
                      <option value="all">All</option>
                      {uniqueAssignees.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Select>
                  </VStack>
                </>
              )}
            </HStack>
          </Flex>

          {viewMode === "del" ? (
            filteredDeliverables.length > 0 ? (
              <DeliverableAccordion
                deliverables={filteredDeliverables}
                onOpen={onBulkModalOpen}
                onRequirementClick={handleOpenRequirement}
                developers={developers}
              />
            ) : (
              <Text>No deliverables found</Text>
            )
          ) : viewMode === "req" ? (
            filteredRequirements.length > 0 ? (
              <RequirementsTable
                requirements={filteredRequirements}
                onRequirementClick={handleOpenRequirement}
                developers={developers}
              />
            ) : (
              <Text>No requirements found</Text>
            )
          ) : filteredRequirements.length > 0 ? (
            <TaskTable tasks={filteredTasks} />
          ) : (
            <Text>No requirements found</Text>
          )}
        </Box>
      )}

      {isBulkModalOpen && (
        <BulkAddRequirementsModal
          isOpen={isBulkModalOpen}
          onClose={onBulkModalClose}
        />
      )}

      {isViewModalOpen && (
        <AddEditRequirementModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          requirement={selectedRequirement}
          type={isNewRequirement ? "new" : null}
        />
      )}
    </Box>
  );
}

export default Tracker;
